<template>
  <div class="UploadDashboard d-flex flex-column w-100 align-items-center">
    <div class="ExportPanelWrapper mt-5 mb-4 flex-grow-1">
      <header class="PanelHeader d-flex justify-content-between py-3 px-4 align-items-center">
        {{ header }}
      </header>

      <Feedback
        v-if="!dismissed"
        :feedback="{ variant: 'logo'}"
        class="mx-4 mt-4 pt-4 pb-4"
        @dismissed="dismissed = true"
      >
        <div style="max-width: 800px">
          {{ $t('best') }} {{ username }},
          <br><br>
          {{ $t('components.export.ExportNationalPanel.msg.line1') }}
          <br>
          <br>
          {{ $t('closingMessage.greetings') }}
          <br>
          {{ $t('closingMessage.evtools') }}
          <br><br>
          <span class="text-muted">
            {{ $t('closingMessage.contact') }} <MailtoSupport />.
          </span>
        </div>

        <b-alert
          show
          variant="warning"
          class="mt-3"
        >
          {{ $t('components.export.ExportNationalPanel.dataAvailableMsg') }}
        </b-alert>
      </Feedback>

      <Feedback
        class="mx-4 mt-4"
        :feedback="feedback"
      />

      <div class="d-flex flex-column p-4">
        <div class="ExportPanel d-flex mt-3 pb-3">
          <aside class="ExportPanel__Aside flex-shrink-0 mr-3">
            <h3 class="ExportPanel__Title">
              {{ $t('components.export.ExportNationalPanel.header') }}
            </h3>
            <div class="w-75 mt-3">
              <p>{{ $t('components.export.ExportNationalPanel.description') }}</p>
            </div>
          </aside>

          <div class="ExportPanel__Main flex-grow-1 mb-3">
            <div class="d-flex align-items-center mt-3 mb-3">
              <span class="ExportPanel__Label mr-3">
                GeoJSON
              </span>

              <b-button
                :disabled="busy"
                variant="cta"
                class="ml-3"
                @click="downloadGeoJSON"
              >
                {{ $t('download') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Feedback from '@/components/form/Feedback'
import MailtoSupport from '@/components/common/MailtoSupport'

import { mapGetters } from 'vuex'
import { saveAs } from 'file-saver'

import { getDateStamp } from '@/helpers/date'
import { statusSlugToNumber } from '@/../shared/services/statusTranslations'
import Vue from 'vue'
import { SOURCE } from '@/services/sourceTranslation'
import { introducedBy } from '@/services/introductionSource'
import { getRolloutPhase } from '@/helpers/phases'
import { splitNumberAndSuffix } from '../../../shared/helpers/address'
import { isString } from 'lodash'

const countryAbbreviationToLabel = {
  NL: 'Nederland',
  BE: 'België',
}

export default {
  name: 'ExportNationalPanel',
  components: {
    MailtoSupport,
    Feedback,
  },
  data() {
    return {
      country: process.env.VUE_APP_DEPLOYMENT.toUpperCase(),
      chargingpoints: [],
      busy: false,

      dismissed: false,
      feedback: {},
    }
  },
  computed: {
    ...mapGetters('access', [
      'getActiveMunicipality',
    ]),
    ...mapGetters('config', ['getPhases', 'getConfigByCode']),
    username() {
      return this.$auth.user.name
    },
    header() {
      return `Export Portaal (${countryAbbreviationToLabel[this.country]})`
    },
    config() {
      return this.getConfigByCode({code: this.getActiveMunicipality})
    }
  },
  methods: {
    /***********************************************************************************
     * Obtain Charging points details
     **********************************************************************************/

    /**
     * Load the charging points
     */

    async fetchData ({ after }) {
      this.busy = true
      this.feedback = {
        message: this.$i18n.t('components.export.ExportNationalPanel.loadingFeedback', { country: countryAbbreviationToLabel[this.country] }),
        variant: 'info',
      }

      const token = await this.$auth.getTokenSilently()

      const api = await fetch('/api/chargingpoints-export', {
        method: 'POST',
        headers: {
          authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({
          source: SOURCE.ECO_MOVEMENT,
          country: this.country,
          after: after || '',
        }),
      })

      if (!api.ok) {
        return Vue.notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'De locaties konden niet worden geladen',
        })
      }

      const response = await api.json()
      console.log(response.total)
      this.chargingpoints = [...this.chargingpoints, ...response.chargingpoints]

      if (response.after) {
        return await this.fetchData({ after })
      }

      this.busy = false
      this.feedback = {
        message: this.$i18n.t('components.export.ExportNationalPanel.dataAvailableFeedback', { country: countryAbbreviationToLabel[this.country] }),
        variant: 'info',
      }

      return this.chargingpoints
    },

    /***********************************************************************************
     * Generate download files
     **********************************************************************************/

    generateGeoJSON() {
      let geojson = {
        type: 'FeatureCollection',
        features: [],
      }

      /**
       * Add the entries to the GEOJSON
       */
      geojson.features = this.chargingpoints.map((entry) => {
        const { number, suffix } = splitNumberAndSuffix({ number: entry.address?.number })
        const code = entry.code

        const properties = {
          id: `${code}-${entry.properties.id}`,
          uuid: `${entry.uuid}`,
          lat: entry.coordinates[1]+''.replace('.', ','),
          lng: entry.coordinates[0]+''.replace('.', ','),
          street: entry.address ? entry.address.street : null,
          streetnumber: number,
          streetsuffix: suffix,
          postalcode: entry.address && entry.address.postalcode ? entry.address.postalcode.replace(/\s/g, '') : null,
          municipality: entry.address ? entry.address.municipality : null,
          city: entry.address ? entry.address.city : null,
          reference: entry.properties.vkb || '',
          source: introducedBy(entry, this.config),
          remark: entry.properties.remark,
          status: entry.properties.status || null,
          statusnumber: statusSlugToNumber({ status: entry.properties.status }),
          statusdescription: this.$i18n.t(`participationPanel.options.statusStations.regularLabels.${entry.properties.status}`),
          rolloutphase: getRolloutPhase(entry, this.getPhases),
          rolloutschedule: entry.prio ? entry.prio.order : null,
          gridconnection: ['definitive'].includes(entry.properties.status) ? '3*25A' : '',
          nroutlets: entry.properties.nroutlets || 2,
          cpo: this.getCpo({ entry }),
        }

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: entry.coordinates.map(coord => isString(coord) ? parseFloat(coord) : coord),
          },
          properties,
        }
      })

      return geojson
    },

    // copied from export panel
    getCpo({ entry }) {
      // Stakholder value (manual input) has a priority over imported one //
      return entry.properties.stakeholders?.find(stakeholder => stakeholder.category === 'cpo')?.name || entry.properties.operator
    },

    async downloadGeoJSON() {
      try {
        await this.fetchData({ after: '' })
        console.log(this.chargingpoints.length, this.chargingpoints[0])
        if (! this.chargingpoints.length) {
          throw Error('No charging points available')
        }

        let content = new Blob([JSON.stringify(this.generateGeoJSON(), null, 4)], { type: 'text/plain;charset=utf-8' })
        saveAs(content, `${this.country}-EcoMovement-Laadpalen-${getDateStamp()}.geojson`)
      } catch (e) {
        console.log(e)

        this.$notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'Probeer het later opnieuw. Als het probleem zich blijft voordoen, neem dan contact op met de support.',
        })
      }
    },
  },
}
</script>

<style lang="scss">


/**
 * This component combines the html & css of three layered admin components,
 *  because it doesn't need any of the logic of those components
 * TODO: Fix html & CSS so that it makes sense as one component
 */
.UploadDashboard {
  &__Panel, &__Select {
    // width: calc(100% - 2rem);
    min-width: 1100px;
    max-width: 1100px;
  }

  &__Panel {
    // border: 1px solid red;
    background: white;
    box-shadow: 0px 1px 3px 0px #dfe2e5;
  }

  .vSelect {
    min-width: 300px;
  }
}

.ExportPanelWrapper {

  min-width: 1100px;
  max-width: 1100px;
  background: white;
  box-shadow: 0px 1px 3px 0px #dfe2e5;

  header {
    background: var(--primary);
    color: white;
    font-size: 1.5rem;
  }

  form {
    label {
      font-size: 1.1rem;
    }

    small {
      outline: none !important;
      font-size: 1rem;
    }
  }
}

.ExportPanel {
  &__Aside {
    width: 275px;
  }

  &__Main {
    font-size: 1.25rem;

    .form-control {
      width: 100px;
      font-size: 1.25rem;
    }

    .multiselect {
      width: 455px;
    }
  }

  &__Label {
    width: 175px;
  }

  &__Input {
    position: relative;
    width: 360px;

    &:after {
      content: attr(data-intructions);
      position: absolute;
      top: 3.1rem;
      font-size: 1rem;
      left: 0;
      color: #7F8FA4;

    }
  }

  &__Download {
    text-decoration: underline;
    color: var(--cta);
  }

  .SvgIcon {
    flex-shrink: 0;
    font-size: 1.75rem;
  }

  .custom-file-input:lang(en) ~ .custom-file-label::after {
    content: 'Selecteer';
  }
}
</style>
